import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import useMobile from '../hooks/useMobile';
import { useTheme } from '../context/ThemeContext';

// ── Swipeable e-post-rad ──────────────────────────────────────
function SwipeableEmailRow({ email, onOpen, onMarkUnread }) {
  const rowRef   = useRef(null);
  const [offset,   setOffset]   = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [animating,setAnimating]= useState(false);
  const [done,     setDone]     = useState(false); // animert ferdig
  // Refs for touch-state (aldri stale i event listeners)
  const t = useRef({ startX:0, startY:0, startOff:0, active:false, dragging:false, cur:0 });

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;

    function onStart(e) {
      const touch = e.touches[0];
      t.current = { startX: touch.clientX, startY: touch.clientY, startOff: t.current.cur, active: true, dragging: false, cur: t.current.cur };
    }

    function onMove(e) {
      if (!t.current.active) return;
      const dx = e.touches[0].clientX - t.current.startX;
      const dy = e.touches[0].clientY - t.current.startY;
      if (!t.current.dragging) {
        if (Math.abs(dy) > Math.abs(dx)) { t.current.active = false; return; }
        if (Math.abs(dx) < 8) return;
        t.current.dragging = true;
      }
      e.preventDefault();
      const clamped = Math.max(-220, Math.min(40, t.current.startOff + dx));
      t.current.cur = clamped;
      setOffset(clamped);
      setRevealed(clamped < -60);
    }

    function triggerUnread() {
      t.current.cur = 0;
      setOffset(0); setRevealed(false); setAnimating(true);
      setTimeout(() => { setAnimating(false); t.current.dragging = false; }, 300);
      onMarkUnread(email.uid);
    }

    function onEnd() {
      if (!t.current.active || !t.current.dragging) { t.current.active = false; return; }
      t.current.active = false;
      const off = t.current.cur;
      setAnimating(true);
      if (off < -170) {
        // Full sveip → merk ulest og snap tilbake
        triggerUnread();
      } else if (off < -60) {
        // Stopp ved avsløringsposisjon (blå ulest-knapp)
        t.current.cur = -88;
        setOffset(-88);
        setRevealed(true);
      } else {
        // Snap tilbake
        t.current.cur = 0;
        setOffset(0);
        setRevealed(false);
      }
      setTimeout(() => { setAnimating(false); t.current.dragging = false; }, 320);
    }

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove',  onMove,  { passive: false });
    el.addEventListener('touchend',   onEnd,   { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove',  onMove);
      el.removeEventListener('touchend',   onEnd);
    };
  }, [email.uid, onMarkUnread]);

  const handleClick = () => {
    if (t.current.dragging) return;
    if (revealed) {
      t.current.cur = 0;
      setOffset(0); setRevealed(false); setAnimating(true);
      setTimeout(() => setAnimating(false), 300);
      return;
    }
    onOpen(email);
  };

  if (done) return null;

  return (
    <div style={{ position:'relative', marginBottom:6, borderRadius:12, overflow:'hidden' }}>
      {/* Blå ulest-bakgrunn */}
      <div style={{
        position:'absolute', inset:0, borderRadius:12,
        background: revealed ? '#3B82F6' : 'rgba(59,130,246,0)',
        display:'flex', alignItems:'center', justifyContent:'flex-end',
        paddingRight:18, transition:'background 0.18s',
      }}>
        {revealed && (
          <button
            onClick={e => { e.stopPropagation(); t.current.cur=0; setOffset(0); setRevealed(false); setAnimating(true); setTimeout(()=>setAnimating(false),300); onMarkUnread(email.uid); }}
            style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2, background:'none', border:'none', cursor:'pointer', color:'white', padding:'4px 8px' }}>
            <span style={{ fontSize:22 }}>📩</span>
            <span style={{ fontSize:11, fontWeight:700 }}>Ulest</span>
          </button>
        )}
      </div>

      {/* E-post-rad */}
      <div ref={rowRef} onClick={handleClick} style={{
        transform: `translateX(${offset}px)`,
        transition: animating ? 'transform 0.3s cubic-bezier(.4,0,.2,1)' : 'none',
        willChange: 'transform',
        display:'flex', alignItems:'flex-start', gap:12, padding:'12px 14px',
        background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:12,
        cursor:'pointer', userSelect:'none', WebkitUserSelect:'none',
        borderLeft: `3px solid ${email.unread ? 'var(--accent)' : 'transparent'}`,
      }}>
        <div style={{ width:40, height:40, borderRadius:'50%', background:stringToColor(email.from||''), display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:15, fontWeight:700, color:'white', pointerEvents:'none' }}>
          {(email.from?.[0]||'?').toUpperCase()}
        </div>
        <div style={{ flex:1, minWidth:0, pointerEvents:'none' }}>
          <div style={{ display:'flex', justifyContent:'space-between', gap:8, marginBottom:2 }}>
            <span style={{ fontSize:13, fontWeight:email.unread?700:500, color:'var(--txt)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>
              {email.from||'Ukjent'}
            </span>
            <span style={{ fontSize:11, color:'var(--txl)', flexShrink:0 }}>{formatDate(email.date)}</span>
          </div>
          <div style={{ fontSize:13, fontWeight:email.unread?600:400, color:email.unread?'var(--txt)':'var(--txm)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:2 }}>
            {email.subject}
          </div>
          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
            {email.unread && <span style={{ width:7, height:7, borderRadius:'50%', background:'var(--accent)', display:'inline-block', flexShrink:0 }}/>}
            {email.hasAttachment && <span style={{ fontSize:11, color:'var(--txl)' }}>📎</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Loading-skeleton ─────────────────────────────────
function EmailSkeleton() {
  return (
    <div>
      {[...Array(6)].map((_, i) => (
        <div key={i} style={{ display:'flex', gap:12, padding:'12px 14px', background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:12, marginBottom:6 }}>
          <div style={{ width:40, height:40, borderRadius:'50%', background:'var(--bdr)', flexShrink:0, animation:'pulse 1.5s ease-in-out infinite' }}/>
          <div style={{ flex:1 }}>
            <div style={{ height:12, width:'40%', background:'var(--bdr)', borderRadius:6, marginBottom:8, animation:'pulse 1.5s ease-in-out infinite' }}/>
            <div style={{ height:11, width:'70%', background:'var(--bdr)', borderRadius:6, animation:'pulse 1.5s ease-in-out infinite' }}/>
          </div>
        </div>
      ))}
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
    </div>
  );
}

const TOKEN = () => localStorage.getItem('token');
const AUTH  = () => ({
  headers: { Authorization: `Bearer ${TOKEN()}` }
});

const PROVIDER_ICONS = {
  gmail: (active) => (
    <svg width="16" height="16" viewBox="0 0 24 24">
      <path fill={active ? 'white' : '#EA4335'}
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill={active ? 'white' : '#34A853'}
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill={active ? 'white' : '#FBBC05'}
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill={active ? 'white' : '#EA4335'}
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  ),
  outlook:    () => <span style={{fontSize:15}}>🔵</span>,
  yahoo:      () => <span style={{fontSize:15}}>🟣</span>,
  icloud:     () => <span style={{fontSize:15}}>☁️</span>,
  protonmail: () => <span style={{fontSize:15}}>🟤</span>,
  custom:     () => <span style={{fontSize:15}}>📧</span>,
};

export default function EmailPage() {
  const isMobile = useMobile();
  const { dark } = useTheme();
  const [accounts,         setAccounts]        = useState([]);
  const [activeAccountId,  setActiveAccountId] = useState(null);
  const [emails,           setEmails]          = useState([]);
  const [total,            setTotal]           = useState(0);
  const [totalPages,       setTotalPages]      = useState(1);
  const [page,             setPage]            = useState(1);
  const [folder,           setFolder]          = useState('INBOX');
  const [loading,          setLoading]         = useState(false);
  const [error,            setError]           = useState('');
  const [selected,         setSelected]        = useState(null);
  const [body,             setBody]            = useState(null);
  const [bodyLoading,      setBodyLoading]     = useState(false);
  const [showCompose,      setShowCompose]     = useState(false);
  const [showAccountMgr,   setShowAccountMgr]  = useState(false);
  const [folders,          setFolders]         = useState([]);
  const [limit,            setLimit]           = useState(10);
  const [lastLoaded,       setLastLoaded]      = useState({});
  const [testingId,        setTestingId]       = useState(null);
  const [testResults,      setTestResults]     = useState({});
  const [showAddAccount,   setShowAddAccount]  = useState(false);
  const [addSaving,        setAddSaving]       = useState(false);
  const [newAcc, setNewAcc] = useState({ name:'', email:'', provider:'imap', username:'', password:'', imap_host:'', imap_port:'993', smtp_host:'', smtp_port:'587' });
  const [compose, setCompose] = useState({
    to:'', subject:'', body:''
  });

  const GMAIL_FOLDERS = [
    { label:'Innboks', value:'INBOX',  icon:'📬' },
    { label:'Sendt',   value:'Sent',   icon:'📤' },
    { label:'Kladd',   value:'Drafts', icon:'📝' },
    { label:'Søppel',  value:'Spam',   icon:'🚫' },
    { label:'Slettet', value:'Trash',  icon:'🗑️' },
  ];

  // Hent kontoer
  useEffect(() => {
    axios.get('/api/email/accounts', AUTH())
      .then(res => {
        const list = res.data || [];
        setAccounts(list);
        if (list.length && !activeAccountId) {
          setActiveAccountId(list[0].id);
        }
      })
      .catch(() => {});
  }, []);

  // Hent e-poster — Gmail caches 5 min, eksplisitt refresh omgår cache
  const loadEmails = useCallback(async (force = false) => {
    if (!activeAccountId) return;

    // For Gmail: hopp over last dersom nylig lastet (< 5 min) og ikke tvungen
    const acc = accounts.find(a => a.id === activeAccountId);
    if (!force && acc?.provider === 'gmail') {
      const cacheKey = `${activeAccountId}|${folder}`;
      const last = lastLoaded[cacheKey];
      if (last && Date.now() - last < 5 * 60 * 1000) return;
    }

    setLoading(true);
    setError('');
    setSelected(null);
    setBody(null);
    try {
      const res = await axios.get(
        `/api/email/messages` +
        `?accountId=${activeAccountId}` +
        `&folder=${encodeURIComponent(folder)}` +
        `&page=${page}&limit=${limit}`,
        AUTH()
      );
      setEmails(res.data.emails || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
      const cacheKey = `${activeAccountId}|${folder}`;
      setLastLoaded(prev => ({ ...prev, [cacheKey]: Date.now() }));
    } catch(err) {
      setError(err.response?.data?.error || 'Kunne ikke hente e-poster');
    } finally {
      setLoading(false);
    }
  }, [activeAccountId, folder, page, limit, accounts, lastLoaded]);

  // Hent mapper når konto byttes
  useEffect(() => {
    setFolder('INBOX');
    setSelected(null);
    setEmails([]);
    setTotal(0);
    // Tøm cache for denne kontoen så e-poster alltid lastes ved kontoskifte
    setLastLoaded(prev => {
      const next = { ...prev };
      Object.keys(next).filter(k => k.startsWith(activeAccountId + '|')).forEach(k => delete next[k]);
      return next;
    });
    if (!activeAccountId) return;
    const acc = accounts.find(a => a.id === activeAccountId);
    if (!acc) return;

    if (acc.provider === 'gmail') {
      setFolders(GMAIL_FOLDERS);
      return;
    }

    axios.get(`/api/email/folders?accountId=${activeAccountId}`, AUTH())
      .then(res => {
        const raw = res.data || [];
        const iconFor = (name) => {
          const n = name.toLowerCase();
          if (n === 'inbox') return '📬';
          if (n.includes('sent')) return '📤';
          if (n.includes('draft')) return '📝';
          if (n.includes('spam') || n.includes('junk')) return '🚫';
          if (n.includes('trash') || n.includes('deleted')) return '🗑️';
          if (n.includes('archive')) return '📦';
          return '📁';
        };
        const labelFor = (name) => {
          const n = name.toLowerCase();
          if (n === 'inbox') return 'Innboks';
          if (n.includes('sent')) return 'Sendt';
          if (n.includes('draft')) return 'Kladd';
          if (n.includes('spam') || n.includes('junk')) return 'Søppel';
          if (n.includes('trash') || n.includes('deleted')) return 'Slettet';
          if (n.includes('archive')) return 'Arkiv';
          return name;
        };
        setFolders(raw.map(f => ({
          label: labelFor(f.name),
          value: f.path || f.name,
          icon:  iconFor(f.name),
        })));
      })
      .catch(() => setFolders(GMAIL_FOLDERS));
  }, [activeAccountId, accounts]);

  useEffect(() => {
    setPage(1);
    setSelected(null);
  }, [folder]);

  useEffect(() => { loadEmails(); }, [loadEmails]);

  // Auto-refresh hvert 5. minutt kun når fanen er aktiv
  const loadEmailsRef = useRef(loadEmails);
  loadEmailsRef.current = loadEmails;
  useEffect(() => {
    if (!activeAccountId) return;
    const iv = setInterval(() => {
      if (!document.hidden) loadEmailsRef.current(true);
    }, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [activeAccountId, folder]);

  // Åpne e-post
  const openEmail = async (email) => {
    setSelected(email);
    setBody(null);
    setBodyLoading(true);
    if (email.unread) {
      try {
        await axios.post(
          `/api/email/messages/${email.uid}/mark`,
          {
            accountId: activeAccountId,
            folder,
            action: 'read'
          },
          AUTH()
        );
        setEmails(prev => prev.map(e =>
          e.uid === email.uid ? {...e, unread:false} : e
        ));
      } catch(e) {}
    }
    try {
      const res = await axios.get(
        `/api/email/messages/${email.uid}` +
        `?accountId=${activeAccountId}` +
        `&folder=${encodeURIComponent(folder)}`,
        AUTH()
      );
      setBody(res.data);
    } catch(err) {
      setBody({ text: 'Kunne ikke laste innhold.', html:'' });
    } finally {
      setBodyLoading(false);
    }
  };

  // Slett — skipConfirm=true brukes fra sveipe-handling (allerede intensjonell)
  const deleteEmail = useCallback(async (uid, skipConfirm = false) => {
    if (!skipConfirm && !window.confirm('Slett denne e-posten?')) return;
    try {
      await axios.delete(
        `/api/email/messages/${uid}` +
        `?accountId=${activeAccountId}` +
        `&folder=${encodeURIComponent(folder)}`,
        AUTH()
      );
      setEmails(prev => prev.filter(e => e.uid !== uid));
      if (selected?.uid === uid) {
        setSelected(null);
        setBody(null);
      }
    } catch(err) {
      alert('Feil: ' + err.message);
    }
  }, [activeAccountId, folder, selected]);

  // Merk som ulest (sveipe-handling)
  const markAsUnread = useCallback(async (uid) => {
    try {
      await axios.post(
        `/api/email/messages/${uid}/mark`,
        { accountId: activeAccountId, folder, action: 'unread' },
        AUTH()
      );
      setEmails(prev => prev.map(e => e.uid === uid ? { ...e, unread: true } : e));
    } catch(err) {
      console.error('Feil ved merking ulest:', err.message);
    }
  }, [activeAccountId, folder]);

  // Send
  const sendEmail = async () => {
    if (!compose.to || !compose.subject) {
      alert('Fyll inn mottaker og emne');
      return;
    }
    try {
      await axios.post('/api/google/gmail/send', {
        to: compose.to,
        subject: compose.subject,
        body: compose.body,
      }, AUTH());
      setShowCompose(false);
      setCompose({ to:'', subject:'', body:'' });
      alert('E-post sendt!');
    } catch(err) {
      alert('Feil: ' + (err.response?.data?.error ||
                        err.message));
    }
  };

  const deleteAccount = async (id, name) => {
    if (!window.confirm(`Fjerne e-postkontoen "${name}"?`)) return;
    try {
      await axios.delete(`/api/email/accounts/${id}`, AUTH());
      const updated = accounts.filter(a => a.id !== id);
      setAccounts(updated);
      if (activeAccountId === id) {
        setActiveAccountId(updated[0]?.id || null);
      }
    } catch (err) {
      alert('Feil ved sletting: ' + (err.response?.data?.error || err.message));
    }
  };

  const testAccount = async (id) => {
    setTestingId(id);
    setTestResults(prev => ({ ...prev, [id]: null }));
    try {
      const res = await axios.post(
        `/api/email/accounts/${id}/test`, {}, AUTH()
      );
      setTestResults(prev => ({
        ...prev,
        [id]: { ok: res.data.ok, message: res.data.message }
      }));
    } catch (err) {
      setTestResults(prev => ({
        ...prev,
        [id]: { ok: false, message: err.response?.data?.message || err.message }
      }));
    } finally {
      setTestingId(null);
    }
  };

  const addAccount = async () => {
    if (!newAcc.name || !newAcc.email) return alert('Navn og e-postadresse er påkrevd');
    setAddSaving(true);
    try {
      const res = await axios.post('/api/email/accounts', newAcc, AUTH());
      const updated = [...accounts, res.data];
      setAccounts(updated);
      if (!activeAccountId) setActiveAccountId(res.data.id);
      setShowAddAccount(false);
      setNewAcc({ name:'', email:'', provider:'imap', username:'', password:'', imap_host:'', imap_port:'993', smtp_host:'', smtp_port:'587' });
    } catch (err) {
      alert('Feil: ' + (err.response?.data?.error || err.message));
    } finally {
      setAddSaving(false);
    }
  };

  const activeAccount = accounts.find(a => a.id === activeAccountId);
  const unread = emails.filter(e => e.unread).length;

  const pageBg = dark ? '#0A0818' : '#EDE8FF';
  const txt    = dark ? '#E8E8F8' : '#1A1A35';
  const sub    = dark ? '#9090B8' : '#888';
  const border = dark ? 'rgba(255,255,255,.07)' : 'rgba(124,111,247,0.12)';

  // ── Delte modaler (mobil + desktop) ──────────────────────────
  const renderCompose = () => (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'flex-end', justifyContent:'flex-end', zIndex:1000, padding:isMobile?0:20, backdropFilter:'blur(4px)' }}>
      <div style={{ background:'var(--card)', borderRadius:isMobile?'20px 20px 0 0':16, width:'100%', maxWidth:520, boxShadow:'0 20px 60px rgba(0,0,0,0.3)', overflow:'hidden' }}>
        <div style={{ display:'flex', alignItems:'center', padding:'13px 16px', background:'#1A1040', color:'white' }}>
          <span style={{ flex:1, fontSize:14, fontWeight:700 }}>
            Ny melding
            {activeAccount && <span style={{ fontSize:12, opacity:0.7, marginLeft:8 }}>fra {activeAccount.email}</span>}
          </span>
          <button onClick={() => setShowCompose(false)} style={{ background:'none', border:'none', color:'white', cursor:'pointer', fontSize:18 }}>✕</button>
        </div>
        <div style={{ padding:16 }}>
          {[{ key:'to', label:'Til', placeholder:'mottaker@example.com', type:'email' }, { key:'subject', label:'Emne', placeholder:'Skriv emne...' }].map(f => (
            <div key={f.key} style={{ display:'flex', alignItems:'center', borderBottom:'1px solid var(--bdr)', padding:'8px 0' }}>
              <label style={{ fontSize:12, color:'var(--txl)', width:50, fontWeight:600, flexShrink:0 }}>{f.label}</label>
              <input value={compose[f.key]} onChange={e => setCompose(c => ({ ...c, [f.key]: e.target.value }))} placeholder={f.placeholder} type={f.type||'text'}
                style={{ flex:1, border:'none', outline:'none', fontSize:14, background:'transparent', color:'var(--txt)', fontFamily:'inherit' }} />
            </div>
          ))}
          <textarea value={compose.body} onChange={e => setCompose(c => ({ ...c, body: e.target.value }))} placeholder="Skriv melding..."
            style={{ width:'100%', minHeight:180, border:'none', outline:'none', fontSize:14, fontFamily:'inherit', color:'var(--txt)', resize:'vertical', padding:'12px 0', background:'transparent', boxSizing:'border-box' }} />
          <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:10, borderTop:'1px solid var(--bdr)' }}>
            <button onClick={sendEmail} style={{ padding:'10px 24px', borderRadius:10, border:'none', background:'var(--accent)', color:'white', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'inherit' }}>Send ↗</button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAccountMgr = () => (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20, backdropFilter:'blur(4px)' }}>
      <div style={{ background:'var(--card)', borderRadius:20, padding:24, width:'100%', maxWidth:480, maxHeight:'85vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:16, fontWeight:800, color:'var(--txt)', margin:0 }}>📧 E-postkontoer</h3>
          <button onClick={() => setShowAccountMgr(false)} style={{ background:'var(--bg2)', border:'none', borderRadius:'50%', width:30, height:30, cursor:'pointer', fontSize:14, color:'var(--txm)' }}>✕</button>
        </div>
        {accounts.map(acc => {
          const Icon = PROVIDER_ICONS[acc.provider] || PROVIDER_ICONS.custom;
          const isActive = acc.id === activeAccountId;
          const testRes  = testResults[acc.id];
          const isTesting = testingId === acc.id;
          return (
            <div key={acc.id} style={{ borderRadius:12, marginBottom:8, border:`1px solid ${isActive?'var(--accent)':'var(--bdr)'}`, overflow:'hidden' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', background:isActive?'var(--bg2)':'transparent', cursor:'pointer' }}
                onClick={() => { setActiveAccountId(acc.id); setShowAccountMgr(false); }}>
                <div style={{ width:36, height:36, borderRadius:10, background:'var(--bg2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><Icon active={false}/></div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:'var(--txt)' }}>{acc.name}</div>
                  <div style={{ fontSize:12, color:'var(--txl)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{acc.email}</div>
                </div>
                <div style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, background:acc.status==='ok'?'var(--green-bg)':'var(--bg2)', color:acc.status==='ok'?'var(--green)':'var(--txl)' }}>
                  {acc.status==='ok'?'✅ OK':'❓'}
                </div>
              </div>
              {testRes && <div style={{ padding:'8px 14px', fontSize:12, background:testRes.ok?'var(--green-bg)':'var(--red-bg)', color:testRes.ok?'var(--green)':'var(--red)' }}>{testRes.ok?'✅ ':'❌ '}{testRes.message}</div>}
              <div style={{ display:'flex', gap:6, padding:'8px 14px', borderTop:'1px solid var(--bdr)', background:'var(--card)' }} onClick={e => e.stopPropagation()}>
                <button onClick={() => testAccount(acc.id)} disabled={isTesting}
                  style={{ flex:1, padding:'6px 10px', borderRadius:8, border:'1px solid var(--bdr)', background:'var(--bg2)', color:'var(--txm)', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit', opacity:isTesting?0.6:1 }}>
                  {isTesting?'⏳ Tester…':'🔌 Test tilkobling'}
                </button>
                <button onClick={() => deleteAccount(acc.id, acc.name)}
                  style={{ padding:'6px 10px', borderRadius:8, border:'1px solid var(--red-bg)', background:'var(--red-bg)', color:'var(--red)', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                  🗑 Fjern
                </button>
              </div>
            </div>
          );
        })}
        {!showAddAccount ? (
          <button onClick={() => setShowAddAccount(true)}
            style={{ width:'100%', padding:'11px', borderRadius:12, marginTop:4, border:'1.5px dashed var(--bdr)', background:'transparent', color:'var(--accent)', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'inherit' }}>
            + Legg til ny konto
          </button>
        ) : (
          <div style={{ marginTop:8, padding:'14px', background:'var(--bg2)', borderRadius:12, display:'flex', flexDirection:'column', gap:8 }}>
            <div style={{ fontWeight:700, fontSize:13, color:'var(--txt)', marginBottom:2 }}>Ny e-postkonto</div>
            {[
              { key:'name',      label:'Visningsnavn',     placeholder:'f.eks. Jobb-mail' },
              { key:'email',     label:'E-postadresse',    placeholder:'deg@eksempel.no' },
              { key:'username',  label:'Brukernavn (IMAP)',placeholder:'samme som e-post' },
              { key:'password',  label:'Passord',          placeholder:'', type:'password' },
              { key:'imap_host', label:'IMAP-server',      placeholder:'imap.eksempel.no' },
              { key:'imap_port', label:'IMAP-port',        placeholder:'993' },
              { key:'smtp_host', label:'SMTP-server',      placeholder:'smtp.eksempel.no' },
              { key:'smtp_port', label:'SMTP-port',        placeholder:'587' },
            ].map(f => (
              <div key={f.key} style={{ display:'flex', flexDirection:'column', gap:2 }}>
                <label style={{ fontSize:11, color:'var(--txm)', fontWeight:600 }}>{f.label}</label>
                <input
                  type={f.type||'text'}
                  value={newAcc[f.key]}
                  onChange={e => setNewAcc(p => ({ ...p, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  style={{ padding:'7px 10px', borderRadius:8, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txt)', fontSize:13, fontFamily:'inherit', outline:'none' }}
                />
              </div>
            ))}
            <div style={{ display:'flex', gap:8, marginTop:4 }}>
              <button onClick={addAccount} disabled={addSaving}
                style={{ flex:1, padding:'8px', borderRadius:8, border:'none', background:'var(--accent)', color:'white', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit', opacity:addSaving?0.6:1 }}>
                {addSaving ? '⏳ Lagrer…' : '✅ Legg til'}
              </button>
              <button onClick={() => setShowAddAccount(false)}
                style={{ padding:'8px 14px', borderRadius:8, border:'1px solid var(--bdr)', background:'transparent', color:'var(--txm)', fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
                Avbryt
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Back-swipe fra venstre kant — håndterer begge tilstander:
  // detalj → e-postliste, liste → forrige rute (navigate(-1))
  // AppShell hopper over /epost, så det er kun denne som styrer tilbake-navigasjon her
  const selectedRef = useRef(selected);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  useEffect(() => {
    let startX = 0, startY = 0;
    const onStart = e => {
      if (e.touches[0].clientX > 25) { startX = 0; return; }
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const onEnd = e => {
      if (!startX) return;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = Math.abs(e.changedTouches[0].clientY - startY);
      startX = 0;
      if (dx > 90 && dy < 100) {
        if (selectedRef.current) {
          setSelected(null);
          setBody(null);
        }
        // Hvis liste-visning: AppShell navigate(-1) er deaktivert for /epost,
        // så gjør ingenting (brukeren bruker bunne-navigasjonen for å bytte side)
      }
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchend',   onEnd,   { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchend',   onEnd);
    };
  }, []);

  // ── MOBIL: E-post detaljvisning ──────────────────────────────
  if (isMobile && selected) {
    return (
      <div style={{ background: pageBg, minHeight: '100%', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
        {/* Hero-header */}
        <div style={{
          background: dark
            ? 'linear-gradient(145deg, #2D1B69 0%, #1A1245 55%, #0D0B1E 100%)'
            : 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)',
          padding: '48px 16px 20px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position:'absolute', top:-40, right:-40, width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
          <button onClick={() => { setSelected(null); setBody(null); }}
            style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.25)', borderRadius:20, padding:'6px 14px', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', marginBottom:14, backdropFilter:'blur(8px)' }}>
            ← Innboks
          </button>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10 }}>
            <div style={{ fontSize:16, fontWeight:800, color:'#fff', lineHeight:1.3, flex:1 }}>{selected.subject}</div>
            <div style={{ display:'flex', gap:6, flexShrink:0 }}>
              <button onClick={() => { setCompose({ to: selected.fromEmail||'', subject:`Re: ${selected.subject}`, body:'' }); setShowCompose(true); }}
                style={{ background:'rgba(255,255,255,0.18)', border:'none', borderRadius:10, padding:'7px 12px', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>↩️ Svar</button>
              <button onClick={() => deleteEmail(selected.uid)}
                style={{ background:'rgba(229,83,75,0.35)', border:'none', borderRadius:10, padding:'7px 10px', color:'#fff', fontSize:14, cursor:'pointer' }}>🗑️</button>
            </div>
          </div>
          {/* Avsender-rad */}
          <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:14, background:'rgba(255,255,255,0.12)', borderRadius:12, padding:'10px 12px' }}>
            <div style={{ width:36, height:36, borderRadius:'50%', background:stringToColor(selected.from||''), display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:700, color:'white', flexShrink:0 }}>
              {(selected.from?.[0]||'?').toUpperCase()}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selected.from}</div>
              {selected.fromEmail && selected.fromEmail !== selected.from && (
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.65)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selected.fromEmail}</div>
              )}
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.55)' }}>
                {new Date(selected.date).toLocaleString('nb-NO', { weekday:'short', day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
              </div>
            </div>
          </div>
        </div>

        {/* Innholdsområde */}
        <div style={{ padding:'12px 14px 24px' }}>
          <div className="glass-card" style={{ padding: body?.html ? 0 : 16, overflow:'hidden', minHeight:120 }}>
            {bodyLoading ? (
              <div style={{ textAlign:'center', padding:'40px 20px', color:sub }}>
                <div style={{ width:28, height:28, margin:'0 auto 10px', border:'3px solid '+border, borderTop:'3px solid #7C6FF7', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
                Laster innhold...
              </div>
            ) : body?.html ? (
              <iframe srcDoc={body.html}
                style={{ width:'100%', border:'none', minHeight:300, display:'block', background:'white' }}
                sandbox="allow-same-origin" title="E-post"
                onLoad={e => { const h = e.target.contentDocument?.body?.scrollHeight; if (h) e.target.style.height = h + 32 + 'px'; }}
              />
            ) : (
              <pre style={{ fontSize:14, color:txt, lineHeight:1.7, whiteSpace:'pre-wrap', fontFamily:'inherit', margin:0 }}>
                {body?.text || 'Ingen innhold'}
              </pre>
            )}
          </div>
        </div>

        {showCompose && renderCompose()}
      </div>
    );
  }

  // ── DESKTOP: E-post detaljvisning ─────────────────────────────
  if (selected) {
    return (
      <div style={{ maxWidth: 800 }}>
        <button onClick={() => { setSelected(null); setBody(null); }}
          style={{ display:'flex', alignItems:'center', gap:6, marginBottom:16, background:'none', border:'none', color:'var(--accent)', cursor:'pointer', fontSize:14, fontWeight:700, fontFamily:'inherit', padding:0 }}>
          ← Tilbake til innboks
        </button>

        <div style={{ background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:14, padding:20, marginBottom:12 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, marginBottom:12 }}>
            <h2 style={{ fontSize:17, fontWeight:800, color:'var(--txt)', margin:0, flex:1 }}>{selected.subject}</h2>
            <div style={{ display:'flex', gap:6 }}>
              <button onClick={() => { setCompose({ to:selected.fromEmail||'', subject:`Re: ${selected.subject}`, body:'' }); setShowCompose(true); }} style={s.actionBtn}>↩️ Svar</button>
              <button onClick={() => deleteEmail(selected.uid)} style={{ ...s.actionBtn, background:'var(--red-bg)', color:'var(--red)', border:'none' }}>🗑️</button>
            </div>
          </div>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <div style={{ width:40, height:40, borderRadius:'50%', background:'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:700, color:'white', flexShrink:0 }}>
              {(selected.from?.[0]||'?').toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize:14, fontWeight:600, color:'var(--txt)' }}>{selected.from}</div>
              {selected.fromEmail && selected.fromEmail !== selected.from && <div style={{ fontSize:12, color:'var(--txl)' }}>{selected.fromEmail}</div>}
              <div style={{ fontSize:12, color:'var(--txl)' }}>
                {new Date(selected.date).toLocaleString('nb-NO', { weekday:'short', day:'numeric', month:'long', year:'numeric', hour:'2-digit', minute:'2-digit' })}
              </div>
            </div>
          </div>
        </div>

        <div style={{ background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:14, padding:20, minHeight:200, overflow:'hidden' }}>
          {bodyLoading ? (
            <div style={{ textAlign:'center', padding:40, color:'var(--txl)' }}>
              <div style={{ width:32, height:32, margin:'0 auto 12px', border:'3px solid var(--bdr)', borderTop:'3px solid var(--accent)', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
              Laster innhold...
            </div>
          ) : body?.html ? (
            <iframe srcDoc={body.html}
              style={{ width:'100%', border:'none', minHeight:400, borderRadius:8, background:'white', display:'block' }}
              sandbox="allow-same-origin" title="E-post"
              onLoad={e => { const h = e.target.contentDocument?.body?.scrollHeight; if (h) e.target.style.height = h + 40 + 'px'; }}
            />
          ) : (
            <pre style={{ fontSize:14, color:'var(--txt)', lineHeight:1.7, whiteSpace:'pre-wrap', fontFamily:'inherit', margin:0 }}>
              {body?.text || 'Ingen innhold'}
            </pre>
          )}
        </div>
        {showCompose && renderCompose()}
      </div>
    );
  }

  // ── MOBIL: Innboksvisning ─────────────────────────────────────
  if (isMobile) return (
    <div style={{ background: pageBg, minHeight: '100%', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      {/* Hero */}
      <div style={{
        background: dark
          ? 'linear-gradient(145deg, #2D1B69 0%, #1A1245 55%, #0D0B1E 100%)'
          : 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)',
        padding: '48px 16px 16px',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-60, left:-20, width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }}/>
        <div style={{ position:'relative', zIndex:1, display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
          <div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', fontWeight:500, marginBottom:2 }}>Famora</div>
            <div style={{ fontSize:24, fontWeight:800, color:'#fff' }}>
              E-post
              {unread > 0 && <span style={{ marginLeft:8, fontSize:13, background:'rgba(255,255,255,0.25)', padding:'2px 8px', borderRadius:12, fontWeight:700 }}>{unread} ulest</span>}
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => loadEmails(true)} disabled={loading}
              style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.25)', borderRadius:12, padding:'8px 12px', color:'#fff', fontSize:15, cursor:'pointer', backdropFilter:'blur(8px)' }}>
              🔄
            </button>
            <button onClick={() => setShowCompose(true)}
              style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.25)', borderRadius:12, padding:'8px 16px', color:'#fff', fontWeight:700, fontSize:14, cursor:'pointer', backdropFilter:'blur(8px)' }}>
              ✉️ Ny
            </button>
          </div>
        </div>
        {/* Konto-chips */}
        <div style={{ position:'relative', zIndex:1, display:'flex', gap:6, overflowX:'auto', scrollbarWidth:'none', paddingBottom:2 }}>
          {accounts.map(acc => {
            const isActive = acc.id === activeAccountId;
            const Icon = PROVIDER_ICONS[acc.provider] || PROVIDER_ICONS.custom;
            return (
              <button key={acc.id} onClick={() => setActiveAccountId(acc.id)}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:20, flexShrink:0, border:'none', background: isActive?'rgba(255,255,255,0.9)':'rgba(255,255,255,0.15)', color: isActive?'#7C6FF7':'#fff', cursor:'pointer', fontSize:12, fontWeight:700, backdropFilter:'blur(8px)' }}>
                <Icon active={false}/>
                <span style={{ maxWidth:90, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{acc.name}</span>
              </button>
            );
          })}
          <button onClick={() => setShowAccountMgr(true)}
            style={{ padding:'6px 12px', borderRadius:20, flexShrink:0, border:'none', background:'rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.7)', cursor:'pointer', fontSize:12, backdropFilter:'blur(8px)' }}>
            ⚙️
          </button>
        </div>
      </div>

      {/* Mappe-tabs */}
      <div style={{ display:'flex', gap:6, overflowX:'auto', scrollbarWidth:'none', padding:'10px 14px 2px' }}>
        {folders.map(f => (
          <button key={f.value} onClick={() => setFolder(f.value)}
            style={{ padding:'6px 14px', borderRadius:20, flexShrink:0, border:'none', background: folder===f.value?(dark?'rgba(124,111,247,0.3)':'rgba(124,111,247,0.15)'):(dark?'rgba(255,255,255,0.06)':'rgba(124,111,247,0.07)'), color: folder===f.value?'#7C6FF7':sub, cursor:'pointer', fontSize:12, fontWeight:700, fontFamily:'inherit' }}>
            {f.icon} {f.label}
          </button>
        ))}
      </div>

      {/* Innhold */}
      <div style={{ padding:'8px 14px 24px' }}>
        {error && (
          <div style={{ padding:'12px 14px', borderRadius:12, background: dark?'rgba(229,83,75,0.15)':'#FEE2E2', color:'#C0312D', fontSize:13, marginBottom:10, lineHeight:1.5 }}>
            <div style={{ fontWeight:700, marginBottom:4 }}>❌ {error}</div>
            <button onClick={loadEmails} style={{ padding:'4px 12px', borderRadius:8, border:'none', background:'#C0312D', color:'white', cursor:'pointer', fontSize:12, fontFamily:'inherit', fontWeight:700 }}>Prøv igjen</button>
          </div>
        )}
        {loading && <EmailSkeleton />}
        {!loading && !error && emails.length === 0 && (
          <div style={{ textAlign:'center', padding:'50px 20px', color:sub }}>
            <div style={{ fontSize:48, marginBottom:10 }}>📭</div>
            <div style={{ fontSize:15, fontWeight:700, color:txt, marginBottom:4 }}>Ingen e-poster i {folder}</div>
            <div style={{ fontSize:12 }}>{activeAccount?.email}</div>
          </div>
        )}
        {!loading && emails.map(email => (
          <SwipeableEmailRow key={email.uid} email={email} onOpen={openEmail} onMarkUnread={markAsUnread} />
        ))}
        {totalPages > 1 && !loading && (
          <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:8, marginTop:12 }}>
            <button onClick={() => setPage(p => p-1)} disabled={page<=1}
              style={{ ...s.pageBtn, opacity:page>1?1:0.4, padding:'8px 14px', fontSize:12 }}>← Forrige</button>
            <span style={{ fontSize:12, color:sub }}>Side {page}/{totalPages}</span>
            <button onClick={() => setPage(p => p+1)} disabled={page>=totalPages}
              style={{ ...s.pageBtn, opacity:page<totalPages?1:0.4, padding:'8px 14px', fontSize:12 }}>Neste →</button>
          </div>
        )}
      </div>

      {showCompose && renderCompose()}
      {showAccountMgr && renderAccountMgr()}
    </div>
  );

  // ── DESKTOP: Innboksvisning ───────────────────────────────────
  return (
    <div style={{ maxWidth: 800 }}>

      {/* ── Konto-velger ─────────────────────────── */}
      <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap', alignItems:'center' }}>
        {accounts.map(acc => {
          const isActive = acc.id === activeAccountId;
          const Icon = PROVIDER_ICONS[acc.provider] || PROVIDER_ICONS.custom;
          return (
            <button key={acc.id} onClick={() => setActiveAccountId(acc.id)}
              style={{ display:'flex', alignItems:'center', gap:7, padding:'7px 13px', borderRadius:20, flexShrink:0, border:`1.5px solid ${isActive?'var(--accent)':'var(--bdr)'}`, background:isActive?'var(--accent)':'var(--card)', color:isActive?'white':'var(--txt)', cursor:'pointer', fontSize:13, fontWeight:isActive?700:500, fontFamily:'inherit', transition:'all .15s' }}>
              <Icon active={isActive}/>
              <span style={{ maxWidth:110, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{acc.name}</span>
              <div style={{ width:6, height:6, borderRadius:'50%', background:acc.status==='ok'?(isActive?'rgba(255,255,255,0.8)':'var(--green)'):'transparent' }}/>
            </button>
          );
        })}
        <button onClick={() => setShowAccountMgr(true)}
          style={{ padding:'7px 13px', borderRadius:20, border:'1.5px dashed var(--bdr)', background:'transparent', color:'var(--txl)', cursor:'pointer', fontSize:13, fontFamily:'inherit' }}>
          ⚙️ Kontoer
        </button>
      </div>

      {/* ── Handlingsrad ─────────────────────────── */}
      <div style={{ display:'flex', gap:8, marginBottom:14, alignItems:'center' }}>
        <button onClick={() => setShowCompose(true)}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:10, border:'none', background:'var(--accent)', color:'white', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
          ✉️ Skriv ny
        </button>
        <select value={folder} onChange={e => setFolder(e.target.value)}
          style={{ padding:'9px 12px', borderRadius:10, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txt)', fontSize:13, fontFamily:'inherit', cursor:'pointer', outline:'none' }}>
          {folders.map(f => <option key={f.value} value={f.value}>{f.icon} {f.label}</option>)}
        </select>
        <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
          style={{ padding:'9px 10px', borderRadius:10, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txt)', fontSize:12, fontFamily:'inherit', cursor:'pointer', outline:'none' }}>
          {[5,10,20,50].map(n => <option key={n} value={n}>{n} per side</option>)}
        </select>
        <button onClick={() => loadEmails(true)} disabled={loading}
          style={{ padding:'9px 12px', borderRadius:10, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txm)', fontSize:16, cursor:'pointer', fontFamily:'inherit' }}>🔄</button>
        <div style={{ marginLeft:'auto', fontSize:12, color:'var(--txl)' }}>
          {unread > 0 && <span style={{ background:'var(--accent)', color:'white', borderRadius:20, padding:'2px 8px', fontSize:11, fontWeight:700, marginRight:6 }}>{unread} ulest</span>}
          {total > 0 && `${total} totalt`}
        </div>
      </div>

      {/* ── Feilmelding ──────────────────────────── */}
      {error && (
        <div style={{ padding:'14px 16px', borderRadius:12, background:'var(--red-bg)', color:'var(--red)', fontSize:13, marginBottom:12, lineHeight:1.6 }}>
          <div style={{ fontWeight:700, marginBottom:6 }}>❌ {error}</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <button onClick={loadEmails} style={{ padding:'5px 12px', borderRadius:8, border:'none', background:'var(--red)', color:'white', cursor:'pointer', fontSize:12, fontFamily:'inherit', fontWeight:700 }}>Prøv igjen</button>
            {(error.toLowerCase().includes('scope')||error.toLowerCase().includes('authentication')||error.toLowerCase().includes('oauth')) && (
              <a href="/api/google/auth?action=connect" style={{ padding:'5px 12px', borderRadius:8, background:'#4285F4', color:'white', cursor:'pointer', fontSize:12, fontFamily:'inherit', fontWeight:700, textDecoration:'none', display:'inline-block' }}>🔗 Koble til Google på nytt</a>
            )}
          </div>
          {(error.toLowerCase().includes('scope')||error.toLowerCase().includes('authentication')) && (
            <div style={{ marginTop:8, fontSize:12, opacity:0.8 }}>Gmail krever at du kobler til Google på nytt med utvidede tillatelser.</div>
          )}
        </div>
      )}

      {loading && <EmailSkeleton />}

      {!loading && !error && emails.length === 0 && (
        <div style={{ textAlign:'center', padding:'50px 20px', background:'var(--card)', borderRadius:14, border:'1px solid var(--bdr)' }}>
          <div style={{ fontSize:48, marginBottom:10 }}>📭</div>
          <div style={{ fontSize:15, fontWeight:600, color:'var(--txt)', marginBottom:4 }}>Ingen e-poster i {folder}</div>
          <div style={{ fontSize:13, color:'var(--txl)' }}>{activeAccount?.email}</div>
        </div>
      )}

      {!loading && emails.map(email => (
        <SwipeableEmailRow key={email.uid} email={email} onOpen={openEmail} onMarkUnread={markAsUnread} />
      ))}

      {totalPages > 1 && !loading && (
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:10, marginTop:16 }}>
          <button onClick={() => setPage(p=>p-1)} disabled={page<=1} style={{ ...s.pageBtn, opacity:page>1?1:0.4 }}>← Forrige</button>
          <span style={{ fontSize:13, color:'var(--txl)' }}>Side {page} av {totalPages}</span>
          <button onClick={() => setPage(p=>p+1)} disabled={page>=totalPages} style={{ ...s.pageBtn, opacity:page<totalPages?1:0.4 }}>Neste →</button>
        </div>
      )}

      {showCompose && renderCompose()}
      {showAccountMgr && renderAccountMgr()}
    </div>
  );
}

// Hjelpefunksjoner
function formatDate(dateStr) {
  const date = new Date(dateStr);
  const now   = new Date();
  const diff  = now - date;
  const days  = diff / (1000 * 60 * 60 * 24);
  if (days < 1) return date.toLocaleTimeString('nb-NO',{
    hour:'2-digit', minute:'2-digit'
  });
  if (days < 7) return date.toLocaleDateString('nb-NO',{
    weekday:'short'
  });
  return date.toLocaleDateString('nb-NO',{
    day:'numeric', month:'short'
  });
}

function stringToColor(str) {
  const colors = [
    '#7C6FF7','#F7605C','#34D399','#FBBF24',
    '#60A5FA','#F472B6','#A78BFA','#FB923C',
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

const s = {
  actionBtn: {
    padding: '6px 12px', borderRadius: 8,
    border: '1px solid var(--bdr)',
    background: 'var(--bg2)', color: 'var(--txm)',
    fontSize: 12, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  pageBtn: {
    padding: '8px 16px', borderRadius: 10,
    border: '1px solid var(--bdr)',
    background: 'var(--card)', color: 'var(--txm)',
    fontSize: 13, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
};
